import { Injectable, Injector } from '@angular/core';
import { ClientLoggerService } from './client-logger.service';
import { LogCategory } from './client-log.models';
import { containsNetworkFailure } from './network-failure';

/**
 * Interceptor für console.error() Aufrufe
 * Fängt alle Fehler ab, die direkt an console.error() gesendet werden
 * und leitet sie an das Logging-System weiter
 */
@Injectable({
  providedIn: 'root'
})
export class ConsoleErrorInterceptorService {
  private originalConsoleError: any;
  private isPatched = false;

  constructor(private injector: Injector) {}

  /**
   * Aktiviert das Console-Error-Intercepting
   */
  initialize(): void {
    if (this.isPatched) {
      return;
    }

    // Speichere originale console.error Funktion
    this.originalConsoleError = console.error.bind(console);

    // Überschreibe console.error
    console.error = (...args: any[]) => {
      // Originale console.error aufrufen (für Entwickler)
      this.originalConsoleError(...args);

      // An Logging-System weiterleiten
      this.logToBackend(args);
    };

    this.isPatched = true;
    console.log('[ConsoleErrorInterceptor] Initialized - console.error() wird jetzt geloggt');
  }

  /**
   * Sendet console.error Daten an Backend
   */
  private logToBackend(args: any[]): void {
    try {
      // Lazy-Loading des ClientLoggerService um zirkuläre Abhängigkeiten zu vermeiden
      const logger = this.injector.get(ClientLoggerService);

      // Konvertiere Arguments zu lesbarem String
      const message = this.formatConsoleArgs(args);

      // Netzabbruch beim Kunden (status 0 / Failed to fetch) ist kein Systemfehler → Warning, nicht Error
      if (containsNetworkFailure(args)) {
        logger.logWarning(message, LogCategory.Network, {
          componentName: 'Console',
          source: 'console.error',
          networkFailure: true,
        });
        return;
      }

      // Prüfe ob es sich um einen Error-Objekt handelt
      const firstArg = args[0];
      if (firstArg instanceof Error) {
        // Logge als Error mit Stack-Trace
        logger.logError(firstArg, {
          category: LogCategory.Error,
          componentName: 'Console',
          properties: {
            source: 'console.error',
            arguments: args.slice(1).map(arg => this.safeStringify(arg))
          }
        });
      } else {
        // Logge als generischer Error
        logger.logError(message, {
          category: LogCategory.Error,
          componentName: 'Console',
          properties: {
            source: 'console.error',
            arguments: args.map(arg => this.safeStringify(arg))
          }
        });
      }
    } catch (error) {
      // Falls beim Logging selbst ein Fehler auftritt, nicht erneut loggen
      // Nur in originale console ausgeben
      this.originalConsoleError('[ConsoleErrorInterceptor] Failed to log error:', error);
    }
  }

  /**
   * Formatiert console.error Arguments zu String
   */
  private formatConsoleArgs(args: any[]): string {
    return args
      .map(arg => {
        if (arg instanceof Error) {
          return `${arg.name}: ${arg.message}`;
        }
        return this.safeStringify(arg);
      })
      .join(' ');
  }

  /**
   * Sichere JSON-Stringification (verhindert Circular-Reference Errors)
   */
  private safeStringify(obj: any): string {
    try {
      if (typeof obj === 'string') {
        return obj;
      }
      if (typeof obj === 'number' || typeof obj === 'boolean') {
        return String(obj);
      }
      if (obj === null || obj === undefined) {
        return String(obj);
      }

      // Spezialbehandlung für Error-Objekte
      if (obj instanceof Error) {
        return `[Error: ${obj.name}] ${obj.message}\nStack: ${obj.stack || 'no stack'}`;
      }

      // Spezialbehandlung für DOM-Elemente
      if (obj instanceof Element || obj instanceof Node) {
        return `[DOM: ${obj.nodeName}] ${(obj as Element).outerHTML?.substring(0, 200) || 'no html'}`;
      }

      // Für Objekte: JSON.stringify mit Circular-Reference Schutz
      const seen = new WeakSet();
      return JSON.stringify(obj, (key, value) => {
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return '[Circular]';
          }
          seen.add(value);
          // Spezialbehandlung für Error innerhalb von Objekten
          if (value instanceof Error) {
            return { errorName: value.name, errorMessage: value.message, errorStack: value.stack?.substring(0, 500) };
          }
        }
        return value;
      });
    } catch (error) {
      return String(obj);
    }
  }

  /**
   * Deaktiviert das Intercepting (für Tests)
   */
  restore(): void {
    if (this.isPatched && this.originalConsoleError) {
      console.error = this.originalConsoleError;
      this.isPatched = false;
      console.log('[ConsoleErrorInterceptor] Restored original console.error()');
    }
  }
}
